/*
 * Copyright © Siemens 2026
 */

import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
} from '@angular/router';
import { filter } from 'rxjs';
import type { BreadcrumbClick } from '@siemens/ix/components';
import { IxBreadcrumb, IxBreadcrumbItem } from '@siemens/ix-angular/standalone';
import { BreadcrumbLabelService } from '../../services/breadcrumb-label.service';

/** A single breadcrumb entry: visible label + the SPA URL it navigates to. */
export interface Crumb {
    label: string;
    url: string;
}

/** Root crumb that always links back to the landing page. */
const HOME_CRUMB: Crumb = { label: 'Home', url: '/' };

/**
 * Section breadcrumb rendered in the app shell.
 *
 * Crumbs are derived from the activated route tree: every route that declares a
 * `data: { breadcrumb: '<label>' }` entry contributes one crumb. A "Home" crumb
 * is prepended so users can step back to the landing page.
 *
 * The strip is hidden entirely when no route in the active tree declares a
 * breadcrumb label (e.g. the landing page and the auth pages), satisfying the
 * requirement that breadcrumbs start at Publishing / Access Management.
 */
@Component({
    selector: 'app-breadcrumb',
    imports: [IxBreadcrumb, IxBreadcrumbItem],
    templateUrl: './breadcrumb.html',
    styleUrl: './breadcrumb.css',
})
export class BreadcrumbComponent {
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly breadcrumbLabels = inject(BreadcrumbLabelService);

    /** Ordered crumbs for the current route, including the leading Home crumb. */
    readonly crumbs = signal<Crumb[]>([]);

    constructor() {
        effect(() => {
            this.breadcrumbLabels.labels();
            this.rebuild();
        });
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.rebuild());
    }

    /** Navigate via the Angular router to keep SPA navigation (no full reload). */
    onItemClick(event: Event): void {
        const detail = (event as CustomEvent<BreadcrumbClick>).detail;
        if (detail?.breadcrumbKey) {
            this.router.navigateByUrl(detail.breadcrumbKey);
        }
    }

    private rebuild(): void {
        const trail = this.collectCrumbs(this.router.routerState.snapshot.root);
        // Only show the strip inside a labelled section; hide on home/auth pages.
        this.crumbs.set(trail.length ? [HOME_CRUMB, ...trail] : []);
    }

    /**
     * Walk the `firstChild` chain from the root, accumulating the resolved URL
     * (param values included) and emitting a crumb for each route that declares
     * a `breadcrumb` label. Unlabelled segments still contribute to the URL.
     */
    private collectCrumbs(root: ActivatedRouteSnapshot): Crumb[] {
        const crumbs: Crumb[] = [];
        let url = '';
        let node: ActivatedRouteSnapshot | null = root;

        while (node) {
            const segment = node.url.map(part => part.path).join('/');
            if (segment) {
                url += `/${segment}`;
            }

            // Use routeConfig.data (per-route) rather than the merged snapshot
            // data, which Angular inherits from parents and would duplicate
            // a parent crumb onto its empty-path child route.
            const fallbackLabel = node.routeConfig?.data?.['breadcrumb'];
            const label = typeof fallbackLabel === 'string'
                ? this.breadcrumbLabels.labelFor(url) ?? fallbackLabel
                : undefined;
            if (typeof label === 'string' && label.length > 0) {
                crumbs.push({ label, url });
            }

            node = node.firstChild;
        }

        return crumbs;
    }
}

export default BreadcrumbComponent;
